import _ from 'lodash';
import React, { useCallback, useContext, useState, useEffect } from 'react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { useModal } from '@jutro/components';
import { BreakpointTrackerContext } from '@jutro/layout';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { WizardPage } from '@xengage/gw-portals-wizard-react';
import { messages as commonMessages } from '@xengage/gw-platform-translations';
import { TranslatorContext } from '@jutro/locale';
import UWCompanyUtil from '../../utils/UWCompanyUtil_TWIA.js';
import SampleCheck from './images/SampleCheck.jpg';
import AgentRegistrationWizardTemplate from '../../templates/AgentRegistrationWizardTemplate.jsx';
import AgentRegistrationService from '../../../../capabilities/ent-capability-agentregistration/services/AgentRegistrationService.js';
import metadata from './CommissionsDirectDepositPage.metadata.json5';
import styles from './CommissionsDirectDepositPage.module.scss';
import messages from './CommissionsDirectDepositPage.messages.js';


function CommissionsDirectDepositPage(props) {

    const { wizardData: registrationVM, updateWizardData, id, isConfirmationPage} = props;

    const breakpoint = useContext(BreakpointTrackerContext)
    const { showAlert, showConfirm } = useModal();
    const { onValidate, isComponentValid } = useValidation(id);
    const [showErrors, setShowErrors] = useState(false);
    const [retrieved, setRetrieved] = useState(false);
    const [loading, setLoading] = useState(false);
    const isTWIA = UWCompanyUtil.isTWIACompany(_.get(registrationVM, 'uwcompany'));
    const accountNumber = _.get(registrationVM, 'directDepositInfo.bankAccountNumber.value');
    const missingAccountNumber = accountNumber === undefined || accountNumber === "";
    const checkImagePath = 'directDepositInfo.checkImages.value';
    const checkImages = _.get(registrationVM, checkImagePath);
    const checkImage = _.get(checkImages, '[0]');
    const translator = useContext(TranslatorContext);

    useEffect(() => {
        if (!isConfirmationPage) {
            const myInput = document.getElementsByName('confirmAccountNumber');

            myInput[0].onpaste = () => false;
        }
    }, [isConfirmationPage]);

    // US27982 load existing check images on page load
    useEffect(() => {
        if (!retrieved && registrationVM.value) {
           setLoading(true);
           const {uwcompany, agencyLicenseNumber} = registrationVM.value;
           AgentRegistrationService.retrieveDocuments(agencyLicenseNumber, uwcompany)
           .then((documents) => {
                if (documents && documents.length > 0) {
                    const docArray = documents.slice(documents.length - 1, documents.length);
                    _.set(registrationVM, checkImagePath, docArray);
                } else {
                    // DE17117 reset check images to clear the UI
                    _.set(registrationVM, checkImagePath, []);
                }
                updateWizardData(registrationVM);
                setRetrieved(true);
           })
           .catch(e => {
                showAlert({
                    title: messages.documentCannotUploaded,
                    message: messages.documentCannotUploaded,
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: commonMessages.ok,
                }).catch(_.noop);
            })
            .finally(() => { setLoading(false); });
        }
    }, [registrationVM]);

    const uploadDocument = useCallback(
        async file => {
            const documentMetaDataTemplate = {
                docUID: '001',
                documentType: 'general_drop_file_ext',
                securityType: 'unrestricted',
                status: 'approved',
                author: 'Potential Agent',
                language: 'en_US',
                section: 'correspondence',
                tDIAgencyLicenseNumber_TFPA: `TDI-${_.get(registrationVM, 'agencyLicenseNumber.value')}`,
                uWCompany_ENT: _.get(registrationVM, 'uwcompany.value.code'), 
                name: file.name,
                mimeType: file.type,
                sessionID: '-'
            };

            setLoading(true);
            try {
                const data = await AgentRegistrationService.uploadDocumentTFPA(
                    file,
                    documentMetaDataTemplate
                );
                const documentsArray = _.get(registrationVM, checkImagePath) ?? [];
                documentsArray.push(data);
                _.set(registrationVM, checkImagePath, documentsArray);
                updateWizardData(registrationVM);
            } catch (error) {
                showAlert({
                    title: messages.documentUploadFailed,
                    message: messages.documentCannotUploaded,
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: commonMessages.ok,
                }).catch(_.noop);
            } finally {
                setLoading(false);
            }
        },
        [showAlert, setLoading]
    );

    // US27982 retrieve token, then download, to avoid expiration
    const onDownloadDocument = useCallback(
        async (event, item) => {
            event.stopPropagation();
            if (_.isNil(item)) {
                return null;
            }

            setLoading(true);
            try {
                const {uwcompany, agencyLicenseNumber} = registrationVM.value;
                const docId = item.docUID;
                const token = await AgentRegistrationService.generateDownloadToken(
                    agencyLicenseNumber, docId, uwcompany
                );
                const downloadURL = AgentRegistrationService.getDocumentDownloadLink(docId, token);
                window.location.href = `${downloadURL}&licenseNumber=${agencyLicenseNumber}`;
            } catch (error) {
                showAlert({
                    title: messages.documentCannotUploaded,
                    message: messages.documentCannotUploaded,
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: commonMessages.ok,
                }).catch(_.noop);
            } finally {
                setLoading(false);
            }
        },
        [AgentRegistrationService, registrationVM, setLoading]
    );

    // US27982 delete document functionality
    const onDeleteDocument = useCallback(
        (file, image) => {

            showConfirm({
                title: messages.removeCheckTitle,
                message: translator(messages.removeCheckMessage, {
                    documentname: image.name,
                }),
                status: 'warning',
                icon: 'mi-error-outline',
                confirmButtonText: commonMessages.yesModel,
                cancelButtonText: messages.cancel,
                className: styles.jut__Button__filled,
            }).then(async (results) => {

                if (results === 'cancel' || results === 'close') {
                    return _.noop();
                }

            const {uwcompany, agencyLicenseNumber} = registrationVM.value;
            setLoading(true);
            // DE17117 use correct variable for doc UID
            AgentRegistrationService.deleteDocument(agencyLicenseNumber, image.docUID, uwcompany)
            .then((result) => {
                if (result) {
                    _.set(registrationVM, checkImagePath, []);
                    updateWizardData(registrationVM);
                }
            })
            .catch(e => {
                showAlert({
                    title: messages.documentCannotUploaded,
                    message: messages.documentCannotUploaded,
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: commonMessages.ok,
                }).catch(_.noop);
            })
            .finally(() => { setLoading(false); })
        }, _.noop)
      },
      [registrationVM, showAlert, showConfirm, translator, updateWizardData]
    );

    const handleRequired = useCallback(
        (value, path) => {
            _.set(registrationVM, path, value);
            updateWizardData(registrationVM);
        },
        [registrationVM, updateWizardData]);

    const onNext = useCallback(async () => {
        if (!isComponentValid || (checkImage === undefined && !missingAccountNumber)) {
            setShowErrors(true)
            return false
        }

        // US26498 if direct deposit info is blank, reset the entity
        const ddInfo = registrationVM.directDepositInfo;
        if (!ddInfo.routingNumber.value) {
            _.set(registrationVM, 'directDepositInfo', {}) 
            _.set(registrationVM, 'directDepositInfo.isCommercialAccount', false)
        }

        return registrationVM
    }, [isComponentValid, checkImage, missingAccountNumber, registrationVM])

    const overrideProps = {
        '@field': {
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
            showErrors,
            readOnly: isConfirmationPage
        },
        confirmAccountNumber: {
            showRequired: !!(_.get(registrationVM, 'directDepositInfo.bankAccountNumber.value'))
        },
        routingNumber: {
            showRequired: !!(_.get(registrationVM, 'directDepositInfo.bankAccountNumber.value'))
        },
        //DE15244- visible changes for confirmation page
        directDepositTextTFPA: {
            visible: !isTWIA && !isConfirmationPage,
        },
        directDepositTextTWIA: {
            visible: isTWIA && !isConfirmationPage,
        },
        sampleCheckImg: {
            src: SampleCheck,
            visible: !isConfirmationPage
        },
        tfpaRegistrationInstruction: {
            visible: !isTWIA && !isConfirmationPage
        },
        twiaRegistrationInstruction: {
            visible: isTWIA && !isConfirmationPage
        },
        uploadCheckButton: {
            disabled: missingAccountNumber,
            title: 'Upload Check',
            visible: checkImage === undefined && !loading // US27982 hide upload when a file exists
        },
        infoMessage: {
            message: translator(messages.checkImageMissing),
            visible: checkImage === undefined && !missingAccountNumber
        },
        uploadInstructions: {
            visible : !isConfirmationPage
        },
        sampleCheck: {
            visible: !isConfirmationPage
        },
        uploadDocumenItem: {
            visible: checkImage !== undefined && !loading // US27982 show item when a file exists
        },
        documentNameLink: {
            onClick: (event) => {onDownloadDocument(event, checkImage)},
            content: _.get(checkImage, 'name')
        },
        documentDeleteIcon: {
            onClick: (event) => { onDeleteDocument(event, checkImage) },
            visible: !isConfirmationPage
        },
        documentLoader: {
            visible : loading
        }
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            onUploadDocument: uploadDocument,
            onHandleRequired: handleRequired,
        },
    };

    return (
        isConfirmationPage ? 
        <ViewModelForm
                uiProps={metadata.pageContent}
                model={registrationVM}
                overrideProps={overrideProps}
                onModelChange={updateWizardData}
                onValidationChange={onValidate}
                callbackMap={resolvers.resolveCallbackMap}
                componentMap={resolvers.resolveComponentMap}
                classNameMap={resolvers.resolveClassNameMap}
            /> :

        <WizardPage
            id={id}
            onNext={onNext}
            template={AgentRegistrationWizardTemplate}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={registrationVM}
                overrideProps={overrideProps}
                onModelChange={updateWizardData}
                onValidationChange={onValidate}
                callbackMap={resolvers.resolveCallbackMap}
                componentMap={resolvers.resolveComponentMap}
                classNameMap={resolvers.resolveClassNameMap}
            />
        </WizardPage>
    );
}

export default CommissionsDirectDepositPage;
